import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class AccessibilityObserverService {
    private observer!: MutationObserver | undefined;
    private landmarkCounter = 0;
    private started = false;
    private processTimeout: any;
    private stylesInjected = false;

    constructor(@Inject(DOCUMENT) private document: Document) { }

    public start(): void {
        // Guard: to run only once
        if (this.started) return;

        this.started = true;
        const body = this.document.body as HTMLElement;

        // Apply fixes to the current DOM which is already loaded. 
        this.applyFixesToTree(body);

        // Begin watching for future DOM changes to aplly fixes.
        this.initObserver();
    }

    private applyFixesToTree(root: HTMLElement): void {
        this.applyWcagFixes(root);
        root.querySelectorAll('*').forEach((el) => {
            if (el instanceof HTMLElement) {
                this.applyWcagFixes(el);
            }
        });
    }

    // Disconnect the observer to stop receiving mutations
    public stop(): void {
        if (this.observer) {
            try {
                this.observer.disconnect();
            } finally {
                this.observer = undefined;
            }
        }

        // Clear debounced work
        if (this.processTimeout) {
            clearTimeout(this.processTimeout);
            this.processTimeout = undefined;
        }

        // Reset started flag
        this.started = false;
    }

    // this will catch and fix elements added or changed after initial load
    private initObserver(): void {
        this.observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (node instanceof HTMLElement) {
                            this.applyWcagFixes(node);
                            node.querySelectorAll('*').forEach((child) => {
                                if (child instanceof HTMLElement) {
                                    this.applyWcagFixes(child);
                                }
                            });
                        }
                    });
                }
                else if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement && mutation.attributeName) {
                    this.handleAttributeMutation(mutation.target, mutation.attributeName);
                }
            }

            setTimeout(() => {
                this.fixEmptyOrGenericTableHeaders(this.document.body);
            }, 200);
        });

        this.observer.observe(this.document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['aria-hidden', 'aria-activedescendant', 'aria-expanded']
        });
    }

    private handleAttributeMutation(target: HTMLElement, attr: string) {
        switch (attr) {
            case 'aria-hidden':
                this.fixAriaHiddenFocusable(target);
                break;
            case 'aria-activedescendant':
                this.fixInvalidAriaActivedescendant(target);
                break;
            case 'aria-expanded':
                this.fixInvalidAriaExpanded(target);
                break;
        }
    }

    private applyWcagFixes(el: HTMLElement): void {
        this.fixHeadingOrder(el);
        this.fixTextAlternatives(el);
        this.fixPrimeNgComponents(el);
        this.fixGenericInputs(el);
        this.fixDropdownRoles(el);
        this.fixInvalidAriaRequired(el);
        this.fixScrollableContainers(el);
        this.ensureUniqueNavigationLandmarks(el);
        this.fixMissingLandmarks(el);
        this.fixInvalidAriaSelected(el);
        this.fixInvalidAriaRequired(el);
        this.preferAriaLabelForToggleableRegions(el);
        this.fixAriaLabelOnGenericElements(el);
        this.fixPButtonHostAndNestedInteractive(el);
        this.injectFocusRingStyles();
        this.fixUtilityLayoutContainersLandmarks(el);
        this.fixLabelAssociationsForInputs(el);
        this.fixMultiselectOptionNesting(el);
        this.fixMultiselectWidgetRoles(el);
        this.fixDialogAndTitleHeadings(el);
        this.fixTextareaLabels(el);
        this.fixNestedInteractiveControls();
    }

    /* ------------------------------- Helpers ------------------------------- */
    private setAttrIfMissing(el: Element, name: string, value: string): void {
        if (!el.hasAttribute(name)) el.setAttribute(name, value);
    }

    private ensureId(el: HTMLElement, prefix: string): string {
        if (el.id && el.id.trim() !== '') return el.id;
        const id = `${prefix}-${Math.random().toString(36).slice(2, 8)}`;
        el.id = id;
        return id;
    }

    private onceKeydown(el: HTMLElement, handler: (e: KeyboardEvent) => void): void {
        el.addEventListener('keydown', handler, { once: true });
    }

    /* ----------------------------- Feature fixes --------------------------- */

    // --- PrimeNG components (behavior preserved) ---
    private fixPrimeNgComponents(el: HTMLElement): void {
        // p-radiobutton
        if (el.classList.contains('p-radiobutton')) {
            const input = el.querySelector('input[type="radio"]') as HTMLInputElement | null;
            const labelText = el.getAttribute('data-label') || 'radio option';
            if (input) {
                this.setAttrIfMissing(input, 'aria-label', labelText);
                this.setAttrIfMissing(input, 'role', 'radio');
                this.setAttrIfMissing(input, 'aria-checked', input.checked ? 'true' : 'false');
                input.addEventListener('change', () => {
                    input.setAttribute('aria-checked', input.checked ? 'true' : 'false');
                });
            }
            if (!el.hasAttribute('tabindex') && !el.classList.contains('p-disabled') && !input?.disabled) {
                el.setAttribute('tabindex', '0');
            }
        }

        // p-checkbox
        if (el.classList.contains('p-checkbox')) {
            const box = el.querySelector('.p-checkbox-box') as HTMLElement | null;
            const input = el.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
            const parentInteractive = el.closest('[role="option"], [role="row"], [role="button"], [role="link"]');
            const isNested = !!parentInteractive && parentInteractive !== el;
            if (box) {
                if (!isNested) {
                    this.setAttrIfMissing(box, 'role', 'checkbox');
                    this.setAttrIfMissing(box, 'tabindex', '0');
                    this.setAttrIfMissing(box, 'aria-checked', input?.checked ? 'true' : 'false');
                } else {
                    box.removeAttribute('role');
                    box.removeAttribute('tabindex');
                    box.removeAttribute('aria-checked');
                }
                if (!box.hasAttribute('aria-label')) {
                    const label = input?.getAttribute('aria-label') || 'checkbox option';
                    box.setAttribute('aria-label', label);
                }
            }
        }

        // p-multiselect
        if (el.classList.contains('p-multiselect')) {
            this.setAttrIfMissing(el, 'role', 'combobox');
            this.setAttrIfMissing(el, 'aria-haspopup', 'listbox');
            this.setAttrIfMissing(el, 'aria-expanded', 'false');
            this.setAttrIfMissing(el, 'aria-label', `Multiselect filter ${++this.landmarkCounter}`);
        }

        // p-calendar
        if (el.classList.contains('p-calendar')) {
            const input = el.querySelector('input[type="text"]') as HTMLInputElement | null;
            const popup = el.querySelector('.p-datepicker') as HTMLElement | null;
            if (input) {
                input.setAttribute('role', 'combobox');
                input.setAttribute('aria-haspopup', 'dialog');
                input.setAttribute('aria-expanded', 'false');
                input.setAttribute('aria-label', 'Date picker');
                if (popup && !popup.id) popup.id = `calendar-panel-${Math.random().toString(36).substring(2, 8)}`;
                if (popup?.id) input.setAttribute('aria-controls', popup.id);
            }
        }

        // p-autocomplete
        if (el.classList.contains('p-autocomplete')) {
            const input = el.querySelector('input[type="text"]') as HTMLInputElement | null;
            if (input) {
                input.setAttribute('role', 'combobox');
                input.setAttribute('aria-haspopup', 'listbox');
                input.setAttribute('aria-expanded', 'false');
                input.setAttribute('aria-label', 'Search suggestions');
            }
        }

        // p-datepicker (standalone)
        if (el.classList.contains('p-datepicker')) {
            const input = el.querySelector('input[type="text"]') as HTMLInputElement | null;
            if (input) {
                input.setAttribute('role', 'combobox');
                input.setAttribute('aria-haspopup', 'dialog');
                input.setAttribute('aria-expanded', 'false');
                input.setAttribute('aria-label', 'Date picker');
            }
        }

        // p-dropdown
        if (el.classList.contains('p-dropdown')) {
            this.setAttrIfMissing(el, 'role', 'combobox');
            this.setAttrIfMissing(el, 'aria-haspopup', 'listbox');
            this.setAttrIfMissing(el, 'aria-expanded', 'false');
            this.setAttrIfMissing(el, 'aria-label', 'dropdown menu');
        }

        // p-dropdown in paginator
        if (el.classList.contains('p-dropdown') && el.classList.contains('p-paginator-rpp-options')) {
            const input = el.querySelector('input[type="text"]') as HTMLInputElement | null;
            const trigger = el.querySelector('.p-dropdown-trigger') as HTMLElement | null;
            const label = el.querySelector('.p-dropdown-label') as HTMLElement | null;
            if (input) {
                this.setAttrIfMissing(input, 'role', 'combobox');
                this.setAttrIfMissing(input, 'aria-haspopup', 'listbox');
                this.setAttrIfMissing(input, 'aria-expanded', 'false');
                this.setAttrIfMissing(input, 'aria-label', 'Items per page selector');
                if (!input.hasAttribute('disabled') && el.classList.contains('p-disabled')) input.setAttribute('disabled', '');
            }
            if (trigger && !trigger.hasAttribute('aria-label')) trigger.setAttribute('aria-label', 'Open items per page dropdown');
            if (label && !label.hasAttribute('aria-label')) label.setAttribute('aria-label', 'Selected items per page');
        }

        // p-paginator buttons
        if (el.tagName === 'BUTTON' && el.classList.contains('p-paginator-element')) {
            const map = {
                'p-paginator-first': 'First page',
                'p-paginator-prev': 'Previous page',
                'p-paginator-next': 'Next page',
                'p-paginator-last': 'Last page',
            } as const;
            for (const cls in map) {
                if (el.classList.contains(cls) && !el.hasAttribute('aria-label')) {
                    el.setAttribute('aria-label', map[cls as keyof typeof map]);
                }
            }
            this.setAttrIfMissing(el, 'role', 'button');
            if (!el.hasAttribute('aria-disabled')) {
                const isDisabled = el.hasAttribute('disabled') || el.classList.contains('p-disabled');
                el.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
            }
            if (!el.hasAttribute('tabindex') && !el.hasAttribute('disabled')) el.setAttribute('tabindex', '0');
        }

        // readonly input[type=text] (combobox-like)
        if (
            el.tagName === 'INPUT' &&
            el.getAttribute('type') === 'text' &&
            el.getAttribute('readonly') === 'readonly'
        ) {
            el.setAttribute('role', 'combobox');
            this.setAttrIfMissing(el, 'aria-haspopup', 'listbox');
            this.setAttrIfMissing(el, 'aria-expanded', 'false');
            this.setAttrIfMissing(el, 'aria-label', 'Selection input');
            this.setAttrIfMissing(el, 'tabindex', '0');

            if (!el.hasAttribute('aria-controls')) {
                const isMulti = !!el.closest('.p-multiselect');
                const selector = isMulti
                    ? '.p-multiselect-panel ul[role="listbox"]'
                    : '.p-dropdown-panel ul[role="listbox"], .p-autocomplete-panel ul[role="listbox"]';
                let listbox = this.document.querySelector(selector) as HTMLElement | null;
                if (!listbox) listbox = this.document.querySelector('ul[role="listbox"]') as HTMLElement | null;
                if (listbox) {
                    const id = this.ensureId(listbox, 'listbox');
                    el.setAttribute('aria-controls', id);
                } else {
                    const fallbackId = `listbox-fallback-${(el as HTMLInputElement).id}`;
                    el.setAttribute('aria-controls', fallbackId);
                }
            }
        }

        // p-splitbutton menubutton
        if (el.tagName === 'BUTTON' && el.classList.contains('p-splitbutton-menubutton')) {
            if (el.hasAttribute('aria-aria-haspopup')) el.removeAttribute('aria-aria-haspopup');
            this.setAttrIfMissing(el, 'aria-haspopup', 'menu');
            this.setAttrIfMissing(el, 'aria-expanded', 'false');
            if (!el.hasAttribute('aria-label') || el.getAttribute('aria-label') === 'action') {
                el.setAttribute('aria-label', 'Open split button menu');
            }
            this.setAttrIfMissing(el, 'role', 'button');
            if (!el.hasAttribute('aria-disabled')) {
                const isDisabled = el.hasAttribute('disabled') || el.classList.contains('p-disabled');
                el.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
            }
            if (!el.hasAttribute('tabindex') && !el.hasAttribute('disabled')) el.setAttribute('tabindex', '0');
        }

        // p-table / p-datatable
        if (el.classList.contains('p-table') || el.classList.contains('p-datatable')) {
            const table = el.querySelector('table') as HTMLElement | null;
            if (table && !table.hasAttribute('role')) table.setAttribute('role', 'table');
            table?.querySelectorAll('th')?.forEach((th) => {
                if (!th.hasAttribute('scope')) th.setAttribute('scope', 'col');
            });
        }

        // p-inputnumber
        if (el.classList.contains('p-inputnumber-input') && el.tagName === 'INPUT') {
            const input = el as HTMLInputElement;
            this.setAttrIfMissing(input, 'role', 'spinbutton');
            const val = input.getAttribute('aria-valuenow');
            if (val === 'null' || val === '') {
                input.setAttribute('aria-valuenow', input.value || '0');
            }
        }

        // p-button
        if (el.classList.contains('p-button') && el.tagName === 'BUTTON') {
            const label = el.getAttribute('ng-reflect-label');
            if (!el.hasAttribute('aria-label') && label) el.setAttribute('aria-label', label);
        }
        if (el.classList.contains('p-button-white') && el.getAttribute('role') === 'checkbox') {
            this.setAttrIfMissing(el, 'aria-label', 'Toggle option');
            this.setAttrIfMissing(el, 'title', 'Toggle option');
        }

        // p-inputswitch
        if (el.tagName === 'P-INPUTSWITCH' || el.classList.contains('p-inputswitch')) {
            const input = el.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
            if (input) {
                this.setAttrIfMissing(input, 'role', 'switch');
                this.setAttrIfMissing(input, 'aria-checked', input.checked ? 'true' : 'false');
                if (!input.hasAttribute('aria-label') && !input.hasAttribute('aria-labelledby')) {
                    input.setAttribute('aria-label', 'Toggle option');
                }
                input.addEventListener('keydown', (event: KeyboardEvent) => {
                    if (event.key === ' ' || event.key === 'Enter') {
                        input.click();
                        input.setAttribute('aria-checked', input.checked ? 'true' : 'false');
                        event.preventDefault();
                    }
                });
            }
        }

        // p-multiselect listbox
        if (el.tagName === 'UL' && el.getAttribute('role') === 'listbox') {
            if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
                el.setAttribute('aria-label', 'Available options');
            }
        }

        // listbox child roles
        if (el.tagName === 'LI' && el.closest('ul[role="listbox"]')) {
            if (el.classList.contains('p-multiselect-item') && !el.hasAttribute('role')) el.setAttribute('role', 'option');
            if (el.classList.contains('p-multiselect-item-group') && !el.hasAttribute('role')) el.setAttribute('role', 'group');
            if (el.classList.contains('p-dropdown-empty-message') || el.classList.contains('p-multiselect-empty-message')) {
                el.setAttribute('role', 'option');
                el.setAttribute('aria-disabled', 'true');
                el.setAttribute('tabindex', '-1');
            }
            if (el.classList.contains('p-dropdown-item') && (!el.textContent?.trim() || el.getAttribute('aria-label') === '')) {
                el.setAttribute('aria-label', 'Option');
            }
        }

        // p-tabpanel DL wrap
        if (el.tagName === 'DL') {
            const childDLs = el.querySelectorAll(':scope > dl');
            childDLs.forEach((child) => {
                const wrapperDD = this.document.createElement('dd');
                el.insertBefore(wrapperDD, child);
                wrapperDD.appendChild(child);
            });
        }

        // dialog / alertdialog naming & aria-modal
        if (el.getAttribute('role') === 'dialog' || el.getAttribute('role') === 'alertdialog') {
            this.setAttrIfMissing(el, 'aria-modal', 'true');
            const hasLabel = el.hasAttribute('aria-label');
            const hasLabelledBy = el.hasAttribute('aria-labelledby');
            if (!hasLabel && !hasLabelledBy) {
                const heading = el.querySelector('h1, h2, h3, .p-dialog-title') as HTMLElement | null;
                if (heading) {
                    const id = this.ensureId(heading, 'dialog-heading');
                    el.setAttribute('aria-labelledby', id);
                } else {
                    el.setAttribute('aria-label', 'Confirmation Dialog');
                }
            }
            this.setAttrIfMissing(el, 'title', 'Confirmation Dialog');
        }

        // progress spinner naming & busy
        if (el.tagName === 'P-PROGRESSSPINNER' || el.classList.contains('p-progress-spinner')) {
            const root = el.classList.contains('p-progress-spinner')
                ? el
                : (el.querySelector('.p-progress-spinner[role="progressbar"]') as HTMLElement);
            if (root) {
                const hasName =
                    (!!root.getAttribute('aria-label') && root.getAttribute('aria-label')!.trim() !== '') ||
                    (!!root.getAttribute('aria-labelledby') && root.getAttribute('aria-labelledby')!.trim() !== '') ||
                    (!!root.getAttribute('title') && root.getAttribute('title')!.trim() !== '');
                if (!hasName) {
                    const candidate = root.closest('.field, .card, section')?.querySelector(
                        'h1,h2,h3,h4,h5,h6,.p-card-title,.section-title'
                    ) as HTMLElement | null;
                    if (candidate) {
                        const lblId = this.ensureId(candidate, 'lbl');
                        root.setAttribute('aria-labelledby', lblId);
                    } else {
                        root.setAttribute('aria-label', 'Loading data');
                    }
                    this.setAttrIfMissing(root, 'title', root.getAttribute('aria-label') || 'Loading data');
                }
                this.setAttrIfMissing(root, 'aria-busy', 'true');
            }
        }
    }

    private fixDialogAndTitleHeadings(el: HTMLElement): void {
        const isDialogTitle =
            el.classList.contains('p-dialog-title') ||
            (typeof el.id === 'string' && el.id.endsWith('_header_title'));

        if (!isDialogTitle) return;

        const tag = el.tagName.toLowerCase();
        const isSemanticHeading = /^h[1-6]$/.test(tag);

        if (!isSemanticHeading) {
            this.setAttrIfMissing(el, 'role', 'heading');
            this.setAttrIfMissing(el, 'aria-level', '2');
        }

        const dialogWrapper = el.closest<HTMLElement>('[role="dialog"], [role="alertdialog"], .p-dialog, [data-pc-name="dialog"]');
        if (!dialogWrapper) return;

        const dialogRoot =
            (dialogWrapper.getAttribute('role') === 'dialog' || dialogWrapper.getAttribute('role') === 'alertdialog')
                ? dialogWrapper
                : (dialogWrapper.querySelector<HTMLElement>('[role="dialog"], [role="alertdialog"]') || dialogWrapper);

        const titleId = this.ensureId(el, 'dialog-heading');

        if (!dialogRoot.hasAttribute('aria-labelledby') && !dialogRoot.hasAttribute('aria-label')) {
            dialogRoot.setAttribute('aria-labelledby', titleId);
        }
    }

    private fixTextareaLabels(el: HTMLElement): void {
        if (el.tagName !== 'TEXTAREA') return;

        const textarea = el as HTMLTextAreaElement;

        const hasAriaLabel = textarea.hasAttribute('aria-label') && textarea.getAttribute('aria-label')!.trim() !== '';
        const labelledBy = textarea.getAttribute('aria-labelledby');
        const hasAriaLabelledBy = !!labelledBy && !!this.document.getElementById(labelledBy);
        const hasTitle = textarea.hasAttribute('title') && textarea.getAttribute('title')!.trim() !== '';
        const hasPlaceholder = textarea.hasAttribute('placeholder') && textarea.getAttribute('placeholder')!.trim() !== '';

        if (hasAriaLabel || hasAriaLabelledBy || hasTitle || hasPlaceholder) return;

        const id = this.ensureId(textarea, 'textarea');
        const explicitLabel = this.document.querySelector(`label[for="${id}"]`) as HTMLLabelElement | null;

        if (explicitLabel) {
            this.ensureId(explicitLabel, 'lbl');
            textarea.setAttribute('aria-labelledby', explicitLabel.id);
            return;
        }

        const implicitLabel = textarea.closest('.field, .form-group, .p-field')?.querySelector('label');
        if (implicitLabel instanceof HTMLLabelElement) {
            this.ensureId(implicitLabel, 'lbl');
            textarea.setAttribute('aria-labelledby', implicitLabel.id);
            return;
        }

        const controlName =
            textarea.getAttribute('formcontrolname') ??
            textarea.getAttribute('name') ??
            'textarea input';

        textarea.setAttribute('aria-label', controlName);
    }

    /** Focus ring CSS (unchanged behavior) */
    private injectFocusRingStyles(): void {
        if (this.stylesInjected) return;
        const head = this.document.head || this.document.getElementsByTagName('head')[0];
        if (!head) return;
        const style = this.document.createElement('style');
        style.id = 'a11y-autofix-focus-ring';
        style.textContent = `
       .p-minute-picker .p-link,
       .p-second-picker .p-link,
       .p-ampm-picker .p-link {
       display: inline-flex !important;
       align-items: center;
       justify-content: center;
       
       min-width: 24px !important;
       min-height: 24px !important;
       width: 24px !important;
       height: 24px !important;
       
       padding: 0 !important;
       margin: 4px !important; /* ensures spacing between buttons */
       box-sizing: border-box;
       }
       
       

    /* Ensure datepicker navigation buttons are 32x32px */
    .p-datepicker-prev,
    .p-datepicker-next {
      width: 32px !important;
      height: 32px !important;
      min-width: 32px !important;
      min-height: 32px !important;
    }
       /* Accordion header fix */
    .p-accordion .p-accordion-header-link {
      display: flex !important;
      align-items: center !important;
      min-height: 32px !important;
      padding-top: 6px !important;
      padding-bottom: 6px !important;
    }

    .p-accordion .p-accordion-header-link .p-link {
      min-width: 24px !important;
      min-height: 24px !important;
      margin-left: 6px !important;
    }
    th[aria-sort="ascending"] {
    color: #085c85 !important;
    }
    th[aria-sort="ascending"] .flex.items-center.gap-2 {
    color: #085c85 !important;
    }

    /* Visible focus for PrimeNG checkbox */
    .p-checkbox .p-checkbox-box:focus-visible,
    .p-checkbox.p-focus .p-checkbox-box {
      outline: 2px solid var(--a11y-focus-color, #2563eb) !important;
      outline-offset: 2px;
    }
      .p-togglebutton .p-togglebutton-label {
      color: #334155 !important;
      }
      .p-togglebutton.p-highlight {
       background-color: #BDD8FF !important;
       }
       .p-togglebutton.p-highlight .p-togglebutton-label {
       color: #1E3A8A !important;
       };

    /* Fallback for browsers without :focus-visible */
    .p-checkbox .p-checkbox-box:focus {
      outline: 2px solid var(--a11y-focus-color, #2563eb) !important;
      outline-offset: 2px;
    }

    /* Visible focus for p-virtualscroller */
    .p-virtualscroller:focus-visible {
      outline: 2px solid var(--a11y-focus-color, #2563eb) !important;
      outline-offset: 2px;
    }

    /* Fallback for browsers without :focus-visible */
    .p-virtualscroller:focus {
      outline: 2px solid var(--a11y-focus-color, #2563eb) !important;
      outline-offset: 2px;
    }
  `;
        head.appendChild(style);
        this.stylesInjected = true;
    }

    // prefer aria-label for toggleable regions (behavior identical)
    private preferAriaLabelForToggleableRegions(el: HTMLElement): void {
        if (!el.matches('.p-toggleable-content[role="region"]')) return;
        const labelledby = (el.getAttribute('aria-labelledby') || '').trim();
        if (labelledby) {
            el.removeAttribute('aria-labelledby');
            el.setAttribute('aria-label', `Toggleable content ${++this.landmarkCounter}`);
        }
    }

    // aria-selected normalization
    private fixInvalidAriaSelected(root: HTMLElement): void {
        const withAriaSelected = root.querySelectorAll<HTMLElement>('[aria-selected]');
        withAriaSelected.forEach((el) => {
            const role = el.getAttribute('role') || '';
            const value = (el.getAttribute('aria-selected') || '').trim();
            const allowed = ['option', 'gridcell', 'row', 'tab', 'treeitem'];
            if (!allowed.includes(role)) {
                const isChoicesItem = el.classList.contains('choices__item');
                const choicesContainer =
                    el.closest('.choices') || el.closest('.choices__list') || el.closest('.choices__inner');
                if (isChoicesItem && choicesContainer) {
                    if (role !== 'option') el.setAttribute('role', 'option');
                    const parentList = el.parentElement;
                    if (parentList && parentList.getAttribute('role') !== 'listbox') {
                        parentList.setAttribute('role', 'listbox');
                        if (!parentList.hasAttribute('aria-label') && !parentList.hasAttribute('aria-labelledby')) {
                            parentList.setAttribute('aria-label', 'Selected values');
                        }
                    }
                    if (value !== 'true' && value !== 'false') {
                        el.setAttribute('aria-selected', value ? 'true' : 'false');
                    }
                } else {
                    el.removeAttribute('aria-selected');
                }
            } else {
                if (value !== 'true' && value !== 'false') {
                    el.setAttribute('aria-selected', value ? 'true' : 'false');
                }
            }
        });
    }

    private fixInvalidAriaExpanded(el: HTMLElement): void {
        if (el.hasAttribute('aria-expanded')) {
            const role = el.getAttribute('role') || '';
            const allowedRoles = ['button', 'link', 'combobox', 'menuitem', 'treeitem'];
            if (!allowedRoles.includes(role)) {
                el.removeAttribute('aria-expanded');
            }
        }
    }

    // fix PrimeNG dropdowns
    private fixDropdownRoles(element: HTMLElement): void {
        const wrappers = element.querySelectorAll('.p-dropdown-items-wrapper');
        wrappers.forEach((wrapper) => {
            if (wrapper.getAttribute('role') === 'listbox') {
                wrapper.removeAttribute('role');
                wrapper.setAttribute('role', 'presentation');
            }
            this.setAttrIfMissing(wrapper, 'tabindex', '0');

            const ul = wrapper.querySelector('ul');
            if (ul && !ul.hasAttribute('role')) ul.setAttribute('role', 'listbox');

            ul?.querySelectorAll('li')?.forEach((li) => {
                if (!li.hasAttribute('role')) li.setAttribute('role', 'option');
            });
        });
    }

    private fixMissingLandmarks(el: HTMLElement): void {
        const overlay = el.closest('.p-overlay, .p-datepicker');
        if (overlay && !overlay.closest('main,[role="main"],section[role="region"]')) {
            const overlayEl = overlay as HTMLElement;
            if (!overlayEl.hasAttribute('role')) {
                overlayEl.setAttribute('role', 'region');
            }
            if (!overlayEl.hasAttribute('aria-label') && !overlayEl.hasAttribute('aria-labelledby')) {
                overlayEl.setAttribute('aria-label', 'Popup content');
            }
            this.ensureUniqueAriaLabel(overlayEl);
            return;
        }
        const withinLandmark = el.closest(
            [
                'main',
                '[role="main"]',
                'header',
                '[role="banner"]',
                'footer',
                '[role="contentinfo"]',
                'nav',
                '[role="navigation"]',
                'aside',
                '[role="complementary"]',
                'form',
                '[role="search"]',
                'section[role="region"]',
            ].join(',')
        );
        if (withinLandmark) return;

        let label: string | null = null;
        let targetEl: HTMLElement = el;

        // PrimeNG & common containers
        if (
            el.matches(
                [
                    'p-tablist',
                    'p-tabpanel',
                    'p-fieldset',
                    'p-toolbar',
                    'p-panel',
                    'p-sidebar',
                    'p-card',
                    'p-tabview',
                    'p-tabmenu',
                    'p-paginator',
                    '.app-content-container',
                    '.content-section',
                    '.p-datatable-wrapper',
                    'app-routing-statistic-summary',
                    '.p-overlay',
                    '.p-datepicker-calendar-container',
                    '.p-hour-picker',
                    '.p-separator',
                    '.p-minute-picker',
                    '.p-ampm-picker',
                    '.flatpickr-months',
                    '.flatpickr-innerContainer',
                ].join(',')
            )
        ) {
            label =
                (el.textContent?.trim() || '') ||
                (el.tagName.toLowerCase().replace('p-', '') + ` component ${++this.landmarkCounter}`);
            targetEl = el;
        }
        // listbox/multiselect/dropdown wrappers
        else if (el.matches('p-listbox, p-multiselect, p-dropdown') || el.closest('p-listbox, p-multiselect, p-dropdown')) {
            const wrapper = (el.closest('p-listbox, p-multiselect, p-dropdown') as HTMLElement) || null;
            if (!wrapper) return;
            label =
                (wrapper.textContent?.trim() || '') ||
                (wrapper.tagName.toLowerCase().replace('p-', '') + ` component ${++this.landmarkCounter}`);
            targetEl = wrapper;
        }

        // special summary row
        const summaryRow = el.closest('.items-center.mb-4.flex') as HTMLElement | null;
        if (
            summaryRow &&
            !summaryRow.closest('main,[role="main"],section[role="region"],header,footer,nav,[role="navigation"],aside,[role="complementary"]')
        ) {
            if (!summaryRow.hasAttribute('role')) summaryRow.setAttribute('role', 'region');
            const strongTitle = summaryRow.querySelector('strong')?.textContent?.trim();
            const inactivePill = summaryRow.querySelector('.inactive, .status, .badge')?.textContent?.trim();
            const inferred = strongTitle ? (inactivePill ? `${strongTitle} – ${inactivePill}` : strongTitle) : `Content section ${++this.landmarkCounter}`;
            if (!summaryRow.hasAttribute('aria-label') && !summaryRow.hasAttribute('aria-labelledby')) {
                summaryRow.setAttribute('aria-label', inferred);
            }
            this.ensureUniqueAriaLabel(summaryRow);
            return;
        }

        // existing heading exception
        if (el.matches('h5.mt-4.font-semibold') && !el.closest('main,[role="main"],section[role="region"],header,footer')) {
            const parent = el.parentElement;
            if (parent && !parent.hasAttribute('role')) {
                parent.setAttribute('role', 'region');
                parent.setAttribute('aria-label', 'Recent Authorizations');
                return;
            }
        }

        // apply landmark when candidate found
        if (label && targetEl && !targetEl.closest('section[role="region"]')) {
            if (targetEl.matches('p-tabmenu') || targetEl.tagName.toLowerCase() === 'p-tabmenu') {
                targetEl.setAttribute('role', 'navigation');
                if (!targetEl.hasAttribute('aria-label')) targetEl.setAttribute('aria-label', 'Tab Navigation');
                this.ensureUniqueAriaLabel(targetEl);
            } else {
                targetEl.setAttribute('role', 'region');
                if (!targetEl.hasAttribute('aria-label')) {
                    targetEl.setAttribute('aria-label', `${label} ${++this.landmarkCounter}`);
                }
                this.ensureUniqueAriaLabel(targetEl);
            }
        }
    }

    private ensureUniqueAriaLabel(el: HTMLElement): void {
        if (!el.hasAttribute('aria-label')) return;
        const label = el.getAttribute('aria-label')!.trim();
        if (!label) return;
        const duplicates = this.document.querySelectorAll(`[role="region"][aria-label="${label}"]`);
        if (duplicates.length > 1) {
            el.setAttribute('aria-label', `${label} – section ${duplicates.length}`);
        }
    }

    private fixUtilityLayoutContainersLandmarks(el: HTMLElement): void {
        const isUtility = el.matches('.mb-4, .mb-8, .col-span-4, .p-fluid.col-span-4, .fluid.col-span-4');
        if (!isUtility) return;

        const already = el.closest(
            'main,[role="main"],header,[role="banner"],footer,[role="contentinfo"],nav,[role="navigation"],aside,[role="complementary"],section[role="region"]'
        );
        if (already) return;

        const hasContent = (el.textContent || '').trim().length > 0;
        if (!hasContent) return;

        if (!el.hasAttribute('role')) el.setAttribute('role', 'region');

        let inferred =
            el.querySelector('h1,h2,h3,h4,h5,h6')?.textContent?.trim() ||
            el.querySelector('label')?.textContent?.trim() ||
            el.querySelector('strong,b')?.textContent?.trim() ||
            '';
        if (!inferred) inferred = `Content section ${++this.landmarkCounter}`;

        if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
            el.setAttribute('aria-label', inferred);
        }
        this.ensureUniqueAriaLabel(el);
    }

    private fixLabelAssociationsForInputs(el: HTMLElement): void {
        // <label for="...">
        if (el.tagName === 'LABEL' && el.hasAttribute('for')) {
            const forId = el.getAttribute('for')!;
            const input = this.document.getElementById(forId) as HTMLElement | null;
            if (!el.id) this.ensureId(el, 'lbl');

            if (input && (['INPUT', 'SELECT', 'TEXTAREA'] as string[]).includes(input.tagName)) {
                const labelledBy = (input.getAttribute('aria-labelledby') || '').trim();
                if (labelledBy && !this.document.getElementById(labelledBy)) {
                    input.setAttribute('aria-labelledby', el.id);
                } else if (!labelledBy) {
                    input.setAttribute('aria-labelledby', el.id);
                }
                if (input.hasAttribute('aria-label')) input.removeAttribute('aria-label');
            }
        }

        // inputs present before label
        if (['INPUT', 'SELECT', 'TEXTAREA'].includes(el.tagName)) {
            const input = el as HTMLInputElement;
            const labelledBy = (input.getAttribute('aria-labelledby') || '').trim();
            if (labelledBy && !this.document.getElementById(labelledBy)) {
                input.removeAttribute('aria-labelledby');
            }
            if (input.id) {
                const explicit = this.document.querySelector(`label[for="${input.id}"]`) as HTMLLabelElement | null;
                if (explicit) {
                    if (!explicit.id) this.ensureId(explicit, 'lbl');
                    input.setAttribute('aria-labelledby', explicit.id);
                    if (input.hasAttribute('aria-label')) input.removeAttribute('aria-label');
                }
            }
            if (input.hasAttribute('disabled')) input.setAttribute('aria-disabled', 'true');
            else input.removeAttribute('aria-disabled');
        }
    }

    private fixMultiselectOptionNesting(el: HTMLElement): void {
        if (el.tagName === 'LI' && el.closest('ul[role="listbox"]')) {
            if (!el.hasAttribute('role')) el.setAttribute('role', 'option');

            const ariaChecked = el.getAttribute('aria-checked');
            const ariaSelected = el.getAttribute('aria-selected');
            if (ariaChecked !== null) {
                if (ariaSelected === null) {
                    el.setAttribute('aria-selected', ariaChecked === 'true' ? 'true' : 'false');
                }
                el.removeAttribute('aria-checked');
            } else if (ariaSelected === null) {
                el.setAttribute('aria-selected', 'false');
            }

            const inputs = el.querySelectorAll('input.p-checkbox-input, input[type="checkbox"]');
            inputs.forEach((inp) => {
                const input = inp as HTMLInputElement;
                input.removeAttribute('aria-label');
                input.removeAttribute('aria-labelledby');
                if (input.id) input.removeAttribute('id');
                input.setAttribute('aria-hidden', 'true');
                input.setAttribute('role', 'presentation');
                input.setAttribute('tabindex', '-1');
                input.setAttribute('disabled', '');
                (input.style as any).pointerEvents = 'none';
            });
        }
    }

    private fixMultiselectWidgetRoles(el: HTMLElement): void {
        if (el.classList.contains('p-multiselect')) {
            this.setAttrIfMissing(el, 'role', 'combobox');
            this.setAttrIfMissing(el, 'aria-haspopup', 'listbox');
            this.setAttrIfMissing(el, 'aria-expanded', 'false');
            this.setAttrIfMissing(el, 'aria-label', `Multiselect filter ${++this.landmarkCounter}`);
        }
        if (el.tagName === 'UL' && !el.hasAttribute('role') && el.closest('.p-multiselect-panel')) {
            el.setAttribute('role', 'listbox');
            if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
                el.setAttribute('aria-label', 'Available options');
            }
        }
    }

    private fixPButtonHostAndNestedInteractive(el: HTMLElement): void {
        if (el.tagName === 'P-BUTTON') {
            const innerButton = el.querySelector('button') as HTMLElement | null;
            const hostLooksInteractive =
                el.hasAttribute('role') || el.hasAttribute('tabindex') || el.getAttribute('type') === 'button';
            if (hostLooksInteractive) {
                const attrs = ['aria-controls', 'aria-expanded', 'aria-label', 'aria-labelledby'];
                if (innerButton) {
                    attrs.forEach((a) => {
                        const v = el.getAttribute(a);
                        if (v && !innerButton.hasAttribute(a)) innerButton.setAttribute(a, v);
                        if (v) el.removeAttribute(a);
                    });
                } else {
                    attrs.forEach((a) => el.removeAttribute(a));
                }
                el.removeAttribute('role');
                el.removeAttribute('tabindex');
                el.removeAttribute('type');
            }
        }

        if (el.getAttribute('data-pc-section') === 'host') {
            const hasFocusableDescendant = !!el.querySelector(
                'button, a[href], input, select, textarea, [role="button"], [role="link"], [tabindex="0"]'
            );
            if (hasFocusableDescendant) {
                el.removeAttribute('role');
                el.removeAttribute('tabindex');
                el.removeAttribute('aria-expanded');
                el.removeAttribute('aria-controls');
                el.removeAttribute('aria-label');
                el.removeAttribute('aria-labelledby');
            }
        }
    }

    private fixAriaLabelOnGenericElements(el: HTMLElement): void {
        const isSortIcon = el.tagName === 'P-SORTICON';
        const hasAriaLabel = el.hasAttribute('aria-label') && el.getAttribute('aria-label')!.trim() !== '';
        if (!isSortIcon || !hasAriaLabel) return;

        const hasClick = typeof (el as any).onclick === 'function' || el.getAttribute('onclick') !== null;
        if (hasClick) {
            this.setAttrIfMissing(el, 'role', 'button');
            this.setAttrIfMissing(el, 'tabindex', '0');
            this.onceKeydown(el, (event: KeyboardEvent) => {
                if (event.key === ' ' || event.key === 'Enter') {
                    (el as HTMLElement).click();
                    event.preventDefault();
                }
            });
        } else {
            el.removeAttribute('aria-label');
            el.setAttribute('aria-hidden', 'true');
        }
    }

    private fixAriaHiddenFocusable(el: HTMLElement): void {
        const isHidden = el.getAttribute('aria-hidden') === 'true';
        const focusable = el.querySelectorAll('button, [tabindex], input, select, textarea, a[href]');
        focusable.forEach((node) => {
            if (!(node instanceof HTMLElement)) return;
            if (isHidden) {
                node.setAttribute('tabindex', '-1');
                if (node instanceof HTMLButtonElement || node instanceof HTMLInputElement) {
                    node.disabled = true;
                }
                if (node.tagName === 'A') {
                    const href = node.getAttribute('href');
                    if (href) node.setAttribute('data-original-href', href);
                    node.removeAttribute('href');
                }
            } else {
                node.removeAttribute('tabindex');
                if (node instanceof HTMLButtonElement || node instanceof HTMLInputElement) {
                    node.disabled = false;
                }
                if (node.tagName === 'A') {
                    const originalHref = node.getAttribute('data-original-href');
                    if (originalHref) node.setAttribute('href', originalHref);
                }
            }
        });
    }

    private fixHeadingOrder(el: HTMLElement): void {
        if (el.hasAttribute('data-injected-h1')) return;
        if (el.matches('h1, h2, h3, h4, h5, h6')) {
            const prev = el.previousElementSibling?.closest('h1,h2,h3,h4,h5,h6');
            let level = 2;
            if (prev) {
                const prevLevel = parseInt(prev.tagName.replace('H', ''), 10);
                level = Math.min(prevLevel + 1, 6);
            }
            el.setAttribute('role', 'heading');
            el.setAttribute('aria-level', level.toString());
        }
    }

    private ensureUniqueNavigationLandmarks(root: HTMLElement): void {
        const navs = this.document.querySelectorAll('nav');
        const seen = new Set<string>();
        navs.forEach((nav, index) => {
            const el = nav as HTMLElement;
            this.setAttrIfMissing(el, 'role', 'navigation');
            let label = el.getAttribute('aria-label') || '';
            if (!label || seen.has(label)) {
                label = index === 0 ? 'Primary navigation' : `Navigation section ${index + 1}`;
                el.setAttribute('aria-label', label);
            }
            seen.add(label);
        });
    }

    private fixGenericInputs(el: HTMLElement): void {
        if (el.tagName === 'INPUT') {
            const input = el as HTMLInputElement;
            const name = input.getAttribute('formcontrolname') || input.getAttribute('name') || 'input field';
            this.setAttrIfMissing(input, 'aria-label', name);

            // Ensure an id (used later by labels and controls)
            this.ensureId(input, 'input');

            const label = input.closest('.field')?.querySelector('label') as HTMLLabelElement | null;
            if (label) {
                if (!label.hasAttribute('for')) label.setAttribute('for', input.id);
                input.setAttribute('aria-labelledby', label.textContent?.trim() || name);
            }
        }
    }

    private fixTextAlternatives(el: HTMLElement): void {
        if (el.tagName === 'IMG' && !el.hasAttribute('alt')) {
            el.setAttribute('alt', 'decorative');
        }
        if ((el.tagName === 'BUTTON' || el.tagName === 'A') && el.textContent?.trim() === '') {
            el.setAttribute('aria-label', 'action');
        }
    }

    private fixInvalidAriaActivedescendant(el: HTMLElement): void {
        if (el.hasAttribute('aria-activedescendant')) {
            const targetId = el.getAttribute('aria-activedescendant')!;
            if (!this.document.getElementById(targetId)) {
                el.removeAttribute('aria-activedescendant');
            }
        }
    }

    private fixEmptyOrGenericTableHeaders(element: HTMLElement): void {
        const headers = element.querySelectorAll('th');
        headers.forEach((th, index) => {
            const text = th.textContent?.trim();
            const ariaLabel = th.getAttribute('aria-label')?.trim();
            if (!th.hasAttribute('scope')) th.setAttribute('scope', 'col');
            if (!text && !ariaLabel) {
                const span = this.document.createElement('span');
                span.className = 'sr-only';
                span.textContent = th.getAttribute('data-field') || `Column ${index + 1}`;
                th.appendChild(span);
            } else if (!text && ariaLabel) {
                const span = this.document.createElement('span');
                span.className = 'sr-only';
                span.textContent = ariaLabel;
                th.appendChild(span);
            }
        });
    }

    private fixInvalidAriaRequired(el: HTMLElement): void {
        if (el.hasAttribute('aria-required')) {
            const role = el.getAttribute('role') || '';
            const allowedRoles = ['radiogroup', 'combobox', 'textbox', 'listbox'];
            if (!allowedRoles.includes(role)) {
                const hasRadios = el.querySelector('input[type="radio"]') !== null;
                if (hasRadios) {
                    el.setAttribute('role', 'radiogroup');
                } else {
                    el.removeAttribute('aria-required');
                }
            }
        }
    }
    private fixNestedInteractiveControls(): void {
        const elements = this.document.querySelectorAll('p-button, p-togglebutton');

        elements.forEach((el) => {
            const htmlEl = el as HTMLElement; 

            const innerButton = htmlEl.querySelector('button');
            if (!innerButton) return; 

            htmlEl.removeAttribute('role');
            htmlEl.removeAttribute('tabindex');

            ['aria-label', 'aria-labelledby', 'aria-controls', 'aria-expanded'].forEach(attr => {
                if (htmlEl.hasAttribute(attr)) {
                    innerButton.setAttribute(attr, htmlEl.getAttribute(attr)!);
                    htmlEl.removeAttribute(attr);
                }
            });

            if (!innerButton.innerText.trim() && !innerButton.hasAttribute('aria-label')) {
                innerButton.setAttribute('aria-label', 'Button'); // fallback label
            }
        });
    }

    private fixScrollableContainers(el: HTMLElement): void {
        const role = el.getAttribute('role');
        if (role === 'presentation' || role === 'none') return;
        if (['menubar', 'menu', 'tablist', 'listbox', 'grid', 'tree', 'combobox'].includes(role || '')) return;

        // p-multiselect scrollable list container
        if (el.classList.contains('p-multiselect-list-container') || el.classList.contains('p-select-list-container')) {
            this.setAttrIfMissing(el, 'tabindex', '0');
            this.setAttrIfMissing(el, 'role', 'region');
            if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
                el.setAttribute('aria-label', 'Scrollable options');
            }
            return;
        }

        const isScrollable = el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth;
        if (isScrollable && (el.classList.contains('p-datatable-wrapper') || el.classList.contains('ng-sidebar--left'))) {
            this.setAttrIfMissing(el, 'tabindex', '0');
            this.setAttrIfMissing(el, 'role', 'region');
            if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
                const baseLabel = el.classList.contains('p-datatable-wrapper') ? 'Scrollable data table' : 'Scrollable sidebar';
                this.landmarkCounter++;
                el.setAttribute('aria-label', `${baseLabel} ${this.landmarkCounter}`);
            }
            (el as HTMLElement).style.outline = 'none';
        }
        if (el.matches('.p-autocomplete-list-container')) {
            el.setAttribute('tabindex', '0'); 
        }
    
    }
}